import React from "react";
import { Printer } from "lucide-react";

/**
 * Reusable printable "Add ___" form used by Stocktake / Machinery / Assets.
 *
 * `specs` = [{ label, flex }] — each becomes a bold label + empty box.
 * `starField` = single { label } — rendered in a yellow highlighted box
 *   because it's the field the manager most cares about (qty, serial, …).
 * `noteLabel` — copy for the notes box next to the star field.
 *
 * Renders THREE identical blank forms per A4 page so one printout captures
 * several finds. The print stylesheet strips the toolbar + forces
 * black-on-white when the user hits Print.
 */

const BlankField = ({ label, flex = 1, tall = false }) => (
  <div className="ppf-field" style={{ flex }}>
    <div className="ppf-field-label">{label}</div>
    <div className={`ppf-field-box ${tall ? "ppf-field-box--tall" : ""}`} />
  </div>
);

const SingleForm = ({
  index,
  title,
  eyebrow,
  rows,
  starField,
  noteLabel,
}) => (
  <section className="ppf-form" data-testid={`add-form-${index}`}>
    <header className="ppf-form-header">
      <div>
        <div className="ppf-form-eyebrow">{eyebrow}</div>
        <h2 className="ppf-form-title">
          {title} #{index}
        </h2>
      </div>
      <div className="ppf-form-meta">
        <div>Counted by:</div>
        <div className="ppf-inline-line" />
        <div>Date:</div>
        <div className="ppf-inline-line ppf-inline-line--short" />
      </div>
    </header>

    {rows.map((row, i) => (
      <div className="ppf-row" key={i}>
        {row.map((f) => (
          <BlankField key={f.label} label={f.label} flex={f.flex ?? 1} />
        ))}
      </div>
    ))}

    <div className="ppf-row">
      <div className="ppf-field ppf-field--star" style={{ flex: 2 }}>
        <div className="ppf-field-label ppf-field-label--big">
          {starField.label} <span className="ppf-star">★</span>
        </div>
        <div className="ppf-field-box ppf-field-box--star" />
      </div>
      <div className="ppf-field" style={{ flex: 3 }}>
        <div className="ppf-field-label">{noteLabel}</div>
        <div className="ppf-field-box ppf-field-box--tall" />
      </div>
    </div>

    <footer className="ppf-form-footer">
      For office use — entered in app on{" "}
      <span className="ppf-inline-line ppf-inline-line--tiny" /> by{" "}
      <span className="ppf-inline-line ppf-inline-line--tiny" />
    </footer>
  </section>
);

const PrintableAddForm = ({
  pageTitle,
  toolbarHint,
  eyebrow,
  formTitle,
  rows,
  starField,
  noteLabel,
  copies = 3,
  scope,
  testid = "printable-add-form-page",
}) => {
  const today = new Date().toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  return (
  <div className="ppf-page" data-testid={testid}>
    <div className="ppf-toolbar no-print">
      <button
        type="button"
        className="ppf-print-btn"
        onClick={() => window.print()}
        data-testid="ppf-print-btn"
      >
        <Printer size={16} strokeWidth={2.5} /> Print sheet
      </button>
      <div>
        <div className="ppf-toolbar-title">{pageTitle}</div>
        <div className="ppf-toolbar-hint">{toolbarHint}</div>
      </div>
    </div>

    {/* Printable heading — matches the Stocktake sheet's branded block so
        every printout looks the same when it lands on the manager's
        desk: eyebrow + big title + optional scope chip on the left,
        Date + copies count on the right. */}
    <div className="ppf-page-heading" data-testid="ppf-page-heading">
      <div className="ppf-heading-main">
        <div className="ppf-heading-left">
          <div className="ppf-page-eyebrow">
            Podlab / Footwork · Warehouse
          </div>
          <h1 className="ppf-page-title">{pageTitle || formTitle}</h1>
          {scope && (
            <div className="ppf-scope-chip" data-testid="ppf-scope-chip">
              {scope}
            </div>
          )}
        </div>
        <div className="ppf-heading-right">
          <div className="ppf-heading-label">Date</div>
          <div className="ppf-heading-value">{today}</div>
          <div className="ppf-heading-label ppf-heading-label--mt">Copies</div>
          <div className="ppf-heading-value">{copies}</div>
        </div>
      </div>
      {toolbarHint && <div className="ppf-page-hint">{toolbarHint}</div>}
    </div>

    {Array.from({ length: copies }).map((_, i) => (
      <SingleForm
        key={i}
        index={i + 1}
        title={formTitle}
        eyebrow={eyebrow}
        rows={rows}
        starField={starField}
        noteLabel={noteLabel}
      />
    ))}

    <style>{`
      .ppf-page {
        background: white;
        color: black;
        padding: 20px;
        max-width: 820px;
        margin: 0 auto;
        font-family: "Helvetica Neue", Arial, sans-serif;
      }
      .ppf-toolbar {
        display: flex;
        gap: 12px;
        align-items: center;
        margin-bottom: 16px;
        padding: 10px 12px;
        border: 2px solid black;
        border-radius: 10px;
        background: #FFF7D6;
      }
      .ppf-print-btn {
        border: 2px solid black;
        background: black;
        color: white;
        font-weight: 800;
        padding: 8px 14px;
        border-radius: 8px;
        display: inline-flex;
        align-items: center;
        gap: 6px;
        cursor: pointer;
      }
      .ppf-toolbar-title { font-weight: 900; font-size: 14px; }
      .ppf-toolbar-hint { font-size: 12px; color: #333; }
      .ppf-page-heading {
        padding: 14px 16px 12px;
        margin-bottom: 14px;
        border-bottom: 2px solid black;
        background: white;
        page-break-after: avoid;
      }
      .ppf-heading-main {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 24px;
      }
      .ppf-heading-left { flex: 1; }
      .ppf-heading-right {
        text-align: right;
        font-size: 11px;
        min-width: 160px;
      }
      .ppf-heading-label {
        color: #6B6B6B;
        font-size: 10px;
      }
      .ppf-heading-label--mt { margin-top: 8px; }
      .ppf-heading-value {
        font-weight: 800;
        font-size: 13px;
        color: #111;
      }
      .ppf-page-eyebrow {
        font-size: 10px;
        letter-spacing: 0.16em;
        text-transform: uppercase;
        font-weight: 800;
        color: #6B6B6B;
        margin-bottom: 4px;
      }
      .ppf-page-title {
        font-size: 28px;
        font-weight: 900;
        margin: 0;
        line-height: 1;
      }
      .ppf-scope-chip {
        display: inline-block;
        margin-top: 10px;
        font-weight: 800;
        font-size: 15px;
        padding: 4px 12px;
        border: 2px solid black;
        border-radius: 8px;
        background: #FFF7D6;
      }
      .ppf-page-hint {
        margin-top: 10px;
        font-size: 11px;
        color: #404040;
        line-height: 1.4;
      }
      .ppf-form {
        border: 2px solid black;
        border-radius: 10px;
        padding: 14px 16px;
        margin-bottom: 14px;
        page-break-inside: avoid;
      }
      .ppf-form-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        gap: 12px;
        padding-bottom: 10px;
        border-bottom: 2px solid black;
        margin-bottom: 10px;
      }
      .ppf-form-eyebrow {
        font-size: 10px;
        letter-spacing: 0.14em;
        text-transform: uppercase;
        font-weight: 800;
        color: #607087;
      }
      .ppf-form-title {
        font-size: 20px;
        font-weight: 900;
        margin: 2px 0 0;
      }
      .ppf-form-meta {
        display: grid;
        grid-template-columns: auto 1fr;
        gap: 4px 8px;
        font-size: 11px;
        align-items: center;
        min-width: 220px;
      }
      .ppf-inline-line {
        height: 1px;
        border-bottom: 1.5px solid black;
        min-width: 120px;
      }
      .ppf-inline-line--short { min-width: 80px; }
      .ppf-inline-line--tiny {
        display: inline-block;
        width: 90px;
        border-bottom: 1.5px solid black;
        vertical-align: middle;
      }
      .ppf-row { display: flex; gap: 8px; margin-bottom: 8px; }
      .ppf-field { display: flex; flex-direction: column; }
      .ppf-field-label {
        font-size: 10px;
        font-weight: 800;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: #333;
        margin-bottom: 3px;
      }
      .ppf-field-label--big { font-size: 12px; color: black; }
      .ppf-field-box {
        border: 1.5px solid black;
        border-radius: 6px;
        height: 26px;
        background: white;
      }
      .ppf-field-box--tall { height: 44px; }
      .ppf-field-box--star {
        height: 46px;
        border-width: 2.5px;
        background: #FFF7D6;
      }
      .ppf-field--star .ppf-field-label { color: #8A1F1F; }
      .ppf-star { color: #8A1F1F; font-size: 12px; }
      .ppf-form-footer {
        margin-top: 8px;
        padding-top: 6px;
        border-top: 1px dashed #666;
        font-size: 10px;
        color: #666;
      }
      @media print {
        .no-print, .ppf-toolbar { display: none !important; }
        .ppf-page { padding: 8mm; max-width: none; }
        .ppf-form { margin-bottom: 8mm; }
        body { background: white !important; }
        @page { margin: 8mm; size: A4 portrait; }
      }
    `}</style>
  </div>
  );
};

export default PrintableAddForm;
